import { useEffect, useState } from 'react';
import Router, { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import get from 'lodash/get';
import getHeadingSkin from 'helpers/getHeadingSkin';
import getHeadingTag from 'helpers/getHeadingTag';
import { formatDateForQuery, formatToPageHeadline } from 'helpers/date';
import { cleanRouterQuery, mapObjectToQueryString } from 'helpers/url';
import { useLinkResolver } from 'prismic/helpers/useLinkResolver';
import { EventCategoriesObj, TagesplanItem } from 'pages/api/tagesplanApi.types';
import { getPdfData } from 'components/molecules/TagesPlanPdfDownload/TagesPlanPdfDownload.helper';
import ComponentsRenderer from 'components/templates/partials/ComponentsRenderer';
import { TagesPlanPdfDownload } from 'components/molecules/TagesPlanPdfDownload';
import { Option } from 'components/molecules/Filter/Filter.types';
import { EventBoxBig } from 'components/molecules/EventBoxBig';
import { Separator } from 'components/organisms/Separator';
import Container from 'components/layout/Grid/Container';
import { Filter } from 'components/molecules/Filter';
import { Heading } from 'components/atoms/Heading';
import { Loading } from 'components/atoms/Loading';
import { Breadcrumb } from 'components/molecules/Breadcrumb';
import { CtaDropdown } from 'components/molecules/CtaDropdown';
import { RowFilterStyled } from 'components/molecules/FilterRows';
import { TagesPlanProps } from './OverviewTagesPlan.types';
import {
  HeadingStyled,
  ColumnStyled,
  DatePickerStyled,
  DateStyled,
  RowDateSelectStyled,
  RowDownloadStyled,
  RowLoadingStyled,
  BgMotivTagesplanStyled,
  SortGroup,
} from './OverviewTagesPlan.styles';
import Image from 'next/image';

export const OverviewTagesPlan: React.FC<TagesPlanProps> = ({ events, data }) => {
  const router = useRouter();
  const { t, lang } = useTranslation('common');
  const { locale } = useLinkResolver();
  const [isloading, setIsLoading] = useState(false);

  useEffect(() => {
    const start = () => {
      setIsLoading(true);
    };
    const end = () => {
      setIsLoading(false);
    };
    Router.events.on('routeChangeStart', start);
    Router.events.on('routeChangeComplete', end);
    Router.events.on('routeChangeError', end);
    return () => {
      Router.events.off('routeChangeStart', start);
      Router.events.off('routeChangeComplete', end);
      Router.events.off('routeChangeError', end);
    };
  }, []);

  const options = [
    {
      label: t('all'),
      value: null,
    },
    ...Object.keys(EventCategoriesObj).map((item) => ({
      label: t(item),
      value: item,
    })),
  ];

  const onClickCategory = (selectedOption: Option): void => {
    const categoryValue =
      selectedOption.value !== null
        ? EventCategoriesObj[selectedOption.value as keyof typeof EventCategoriesObj]
        : null;

    const queryObj = cleanRouterQuery(router.query);

    if (categoryValue) {
      queryObj.cat = categoryValue;
    } else {
      delete queryObj.cat;
    }

    const queryString = mapObjectToQueryString(queryObj);
    router.push(`/${lang}${router.asPath.split('?')[0]}${queryString}&lang=${lang}`, undefined);
  };

  const onClickSort = (value: string): void => {
    const queryObj = cleanRouterQuery(router.query);

    if (value) {
      queryObj.sort = value;
    }

    const queryString = mapObjectToQueryString(queryObj);
    router.push(`/${lang}${router.asPath.split('?')[0]}${queryString}&lang=${lang}`, undefined);
  };

  const onClickDatePicker = (date: Date): void => {
    const queryObj = cleanRouterQuery(router.query);
    queryObj.date = formatDateForQuery(date);
    const queryString = mapObjectToQueryString(queryObj);
    router.push(`/${lang}${router.asPath.split('?')[0]}${queryString}&lang=${lang}`, undefined);
  };

  const selectedCategory = (): string | null => {
    const queryObj = cleanRouterQuery(router.query);
    const queryCategoryNumber = queryObj.cat;
    const foundCategoryEntry = Object.entries(EventCategoriesObj).find((categoryEntry) => {
      const [, categoryNumber] = categoryEntry;
      return categoryNumber === queryCategoryNumber;
    });
    if (foundCategoryEntry) {
      return foundCategoryEntry[0];
    }
    return null;
  };

  const isSortSelected = (): string => {
    const queryObj = cleanRouterQuery(router.query);
    const querySort = queryObj.sort;
    const singleQueryCat = Array.isArray(querySort) ? querySort[0] : querySort;
    return singleQueryCat || '';
  };

  const currentDate = (): Date | undefined => {
    const queryObj = cleanRouterQuery(router.query);
    const queryDate = queryObj.date;
    const singleQueryDate = Array.isArray(queryDate) ? queryDate[0] : queryDate;
    if (singleQueryDate) {
      return queryDate ? new Date(singleQueryDate) : new Date();
    }
    return undefined;
  };

  const getContent = () => {
    const dataForPdfContent = {
      items: events?.items,
      date: events?.date,
    };
    return getPdfData(dataForPdfContent, t);
  };

  const sortOptions = [
    {
      label: t('sortTimeAsc'),
      value: 'asc',
    },
    {
      label: t('sortTimeDes'),
      value: 'desc',
    },
  ];

  return (
    <>
      <Container>
        <ColumnStyled span={12}>
          <BgMotivTagesplanStyled>
            <Image
              alt="Tagesplan motiv"
              src="/motivs/tagesplan-motiv.png"
              width={1555}
              height={1962}
            />
          </BgMotivTagesplanStyled>

          <Breadcrumb />

          <HeadingStyled skin={getHeadingSkin('heading1')} elementType={getHeadingTag('heading1')}>
            {t('eventsPageTitle')}
          </HeadingStyled>

          <DateStyled>
            {events && events.date && formatToPageHeadline(events.date, locale)}
          </DateStyled>

          <RowDateSelectStyled>
            {t('selectDate')}{' '}
            <DatePickerStyled currentDate={currentDate()} onChange={onClickDatePicker} />
          </RowDateSelectStyled>

          <RowDownloadStyled>
            <TagesPlanPdfDownload slices={get(data, 'data.body', [])} content={getContent()} />
          </RowDownloadStyled>

          <Heading skin={getHeadingSkin('heading2')} elementType={getHeadingTag('heading2')}>
            {t('ourProgrammToday')}
          </Heading>

          <RowFilterStyled>
            <Filter
              onSelect={onClickCategory}
              selectedOption={selectedCategory()}
              options={options}
            />
            <SortGroup>
              {t('sortBy')}
              <CtaDropdown
                options={sortOptions}
                defaultValue={isSortSelected()}
                onSelectCallback={(value) => {
                  onClickSort(value);
                }}
              />
            </SortGroup>
          </RowFilterStyled>

          {isloading && (
            <RowLoadingStyled>
              <Loading />
            </RowLoadingStyled>
          )}

          {!isloading && !events?.items && <div>{t('noResults')}</div>}
          {events && events.items && events.items.length === 0 && !isloading && (
            <div>{t('noResults')}</div>
          )}

          {events &&
            events.items &&
            events.items.map((eventItem: TagesplanItem, index: number) => (
              <EventBoxBig key={`event-box_${index}`} data={eventItem} />
            ))}
        </ColumnStyled>
      </Container>
      <Separator primary={{ horizontal_line: true }}></Separator>
      <ComponentsRenderer slices={get(data, 'data.body', [])} />
    </>
  );
};
